import type { ReactNode, Ref } from "react";

import {
	Checkbox as AriaCheckbox,
	type CheckboxProps as AriaCheckboxProps,
} from "react-aria-components";

import { cx } from "@/utils/cx";

export interface CheckboxBaseProps {
	className?: string;
	isDisabled?: boolean;
	isFocusVisible?: boolean;
	isIndeterminate?: boolean;
	isSelected?: boolean;
	size?: "md" | "sm";
}

export const CheckboxBase = ({
	className,
	isDisabled,
	isFocusVisible = false,
	isIndeterminate,
	isSelected,
	size = "sm",
}: CheckboxBaseProps) => {
	return (
		<div
			className={cx(
				"flex size-4 shrink-0 cursor-pointer appearance-none items-center justify-center rounded bg-primary ring-1 ring-primary ring-inset",
				size === "md" && "size-5 rounded-md",
				(isSelected || isIndeterminate) && "bg-brand-solid ring-brand-solid",
				isDisabled && "cursor-not-allowed opacity-50",
				isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring",
				className,
			)}
		>
			<svg
				aria-hidden="true"
				className={cx(
					"pointer-events-none absolute h-3 w-2.5 text-fg-white opacity-0 transition-inherit-all",
					size === "md" && "size-3.5",
					isIndeterminate && "opacity-100",
				)}
				fill="none"
				viewBox="0 0 14 14"
			>
				<path
					d="M2.91675 7H11.0834"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
				/>
			</svg>

			<svg
				aria-hidden="true"
				className={cx(
					"pointer-events-none absolute size-3 text-fg-white opacity-0 transition-inherit-all",
					size === "md" && "size-3.5",
					isSelected && !isIndeterminate && "opacity-100",
				)}
				fill="none"
				viewBox="0 0 14 14"
			>
				<path
					d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
				/>
			</svg>
		</div>
	);
};
CheckboxBase.displayName = "CheckboxBase";

interface CheckboxProps extends AriaCheckboxProps {
	hint?: ReactNode;
	isGrouped?: boolean;
	label?: ReactNode;
	ref?: Ref<HTMLLabelElement>;
	size?: "md" | "sm";
}

export const Checkbox = ({
	className,
	hint,
	isGrouped,
	label,
	size = "sm",
	...ariaCheckboxProps
}: CheckboxProps) => {
	const sizes = {
		md: {
			hint: "text-md",
			label: "text-md font-medium",
			root: "gap-3",
			textWrapper: "gap-0.5",
		},
		sm: {
			hint: "text-sm",
			label: "text-sm font-medium",
			root: "gap-2",
			textWrapper: "",
		},
	};

	return (
		<AriaCheckbox
			{...ariaCheckboxProps}
			className={(state) =>
				cx(
					"relative flex items-start",
					state.isDisabled && "cursor-not-allowed",
					sizes[size].root,
					typeof className === "function" ? className(state) : className,
					isGrouped && "rounded-md border border-border-secondary p-2",
					isGrouped && state.isSelected && "border-2 border-border-brand",
				)
			}
		>
			{({ isDisabled, isFocusVisible, isIndeterminate, isSelected }) => (
				<>
					<CheckboxBase
						className={label || hint ? "mt-0.5" : ""}
						isDisabled={isDisabled}
						isFocusVisible={isFocusVisible}
						isIndeterminate={isIndeterminate}
						isSelected={isSelected}
						size={size}
					/>
					{(label || hint) && (
						<div
							className={cx("inline-flex flex-col", sizes[size].textWrapper)}
						>
							{label && (
								<p
									className={cx(
										"text-secondary select-none",
										sizes[size].label,
									)}
								>
									{label}
								</p>
							)}
							{hint && (
								<span
									className={cx("text-tertiary", sizes[size].hint)}
									onClick={(event) => event.stopPropagation()}
								>
									{hint}
								</span>
							)}
						</div>
					)}
				</>
			)}
		</AriaCheckbox>
	);
};
Checkbox.displayName = "Checkbox";
